import { Box, Alert, Typography, Stack, Skeleton } from '@mui/material';
import { AllocatedPartsDatagrid } from 'ui-component/AllocatedPartsDatagrid';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';
import {
  calculateAllocationStats,
  calculateAllocationStatusCounts,
  buildKitRequestRelatedRecords,
} from 'ui-component/AddPartsToKitDialog/components/AllocationReview/utils';
import { useMemo } from 'react';
import StatusSegmentBar from 'ui-component/StatusSegmentBar';
import RelatedRecords from 'ui-component/shared/RelatedRecords';
import { RelatedRecordType } from 'types/inventory';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

export type AllocationSummaryProps = {
  lines: DecoratedKitRequestLine[];
  kitRequest?: KitRequestSubtype | null;
  loading?: boolean;
};

const AllocationSummary = ({
  lines,
  kitRequest,
  loading,
}: AllocationSummaryProps) => {
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();
  const stats = useMemo(() => calculateAllocationStats(lines), [lines]);
  const statusSections = useMemo(
    () => calculateAllocationStatusCounts(lines),
    [lines]
  );

  const relatedRecords = useMemo(
    () => buildKitRequestRelatedRecords(kitRequest),
    [kitRequest]
  );

  const hasUnallocatedLines = stats.partialOrUnallocated > 0;

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 2 }}>
        {kitRequest && relatedRecords.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="h5">Kit Details</Typography>
            {kitRequest.team && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  {handleReplaceWithCustomTeamName('Team')}:
                </Typography>
                <RelatedRecords
                  relatedRecords={relatedRecords.filter(
                    (r) => r.recordType === RelatedRecordType.TEAM
                  )}
                  initialDisplayLimit={10}
                />
              </Stack>
            )}
            {kitRequest.sourceLocations &&
              kitRequest.sourceLocations.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Source Location:
                  </Typography>
                  <RelatedRecords
                    relatedRecords={relatedRecords.filter(
                      (r) => r.recordType === RelatedRecordType.STOCK_LOCATION
                    )}
                    initialDisplayLimit={10}
                  />
                </Stack>
              )}
            {kitRequest.productionRun && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  Program:
                </Typography>
                <RelatedRecords
                  relatedRecords={relatedRecords.filter(
                    (r) => r.recordType === RelatedRecordType.PRODUCTION_RUN
                  )}
                  initialDisplayLimit={10}
                />
              </Stack>
            )}
          </Stack>
        )}

        <Typography variant="body2" color="text.secondary">
          On Hand stock is ready to be kitted. Expected stock has recently been
          purchased and will be kitted as soon as it arrives at Cofactr.
          Unavailable stock cannot be allocated to this kit. To ensure the kit
          is completed, purchase more stock.
        </Typography>

        {loading ? (
          <Skeleton variant="rounded" width="100%" height={24} />
        ) : (
          statusSections.length > 0 && (
            <StatusSegmentBar
              sections={statusSections}
              height={24}
              showLabels
              showTotal
            />
          )
        )}

        {!loading &&
          (hasUnallocatedLines ? (
            <Alert severity="warning">
              Some parts could not be fully allocated. Expand rows below to see
              allocation details and reasons. You may need to adjust stock
              availability, source locations, or team assignments.
            </Alert>
          ) : (
            <Alert severity="success">
              All parts have been successfully allocated! Click
              &ldquo;Done&rdquo; to view the kit details.
            </Alert>
          ))}

        {!loading &&
          hasUnallocatedLines &&
          kitRequest?.shipPartial === false && (
            <Alert severity="error">
              This kit will not ship until all expected parts have been received
              and there is sufficient stock of all unallocated parts because
              partial shipments are not allowed.
            </Alert>
          )}
      </Stack>

      <AllocatedPartsDatagrid
        rows={lines}
        loading={loading}
        kitRequest={kitRequest}
      />
    </Box>
  );
};

export default AllocationSummary;
